import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (this.refs.searchInput.id === 'CollectionSearchInput') {
      const form = this.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
        });
      }

      document.addEventListener('section:render', this.#handleSectionRender);

      const urlParams = new URLSearchParams(window.location.search);
      const q = urlParams.get('q') || '';
      if (q) {
        this.refs.searchInput.value = q;
        this.#showResetButton();
        this.#filterCollectionGrid(q);
      }
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    document.removeEventListener('section:render', this.#handleSectionRender);
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      this.refs.searchInput.blur();
      return;
    }

    const isCollectionSearch = this.refs.searchInput.id === 'CollectionSearchInput';
    if (isCollectionSearch) {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.#filterCollectionGrid(this.refs.searchInput.value);
        this.refs.searchInput.blur();
      }
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          if (this.refs.form && this.refs.form instanceof HTMLFormElement) {
            event.preventDefault();
            this.refs.form.submit();
          } else {
            const searchUrl = new URL(Theme.routes.search_url, location.origin);
            searchUrl.searchParams.set('q', this.refs.searchInput.value);
            window.location.href = searchUrl.toString();
          }
        }
        break;
      }
    }
  };

  /**
   * Handles the click on the collection page search submit button.
   * @param {Event} event - The click event.
   */
  onSearchSubmitClick = (event) => {
    const isCollectionSearch = this.refs.searchInput.id === 'CollectionSearchInput';
    if (isCollectionSearch) {
      event.preventDefault();
      this.#filterCollectionGrid(this.refs.searchInput.value);
      this.refs.searchInput.blur();
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (event && !event.inputType && this.refs.searchInput.id !== 'CollectionSearchInput') return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    const isCollectionSearch = this.refs.searchInput.id === 'CollectionSearchInput';
    if (isCollectionSearch) {
      if (!searchTerm.length) {
        this.#resetSearch();
      } else {
        this.#filterCollectionGrid(searchTerm);
      }
      return;
    }

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const typeInput = this.querySelector('input[name="type"]');
    if (typeInput && typeInput.value) {
      url.searchParams.set('resources[type]', typeInput.value);
    }
    const optionsPrefixInput = this.querySelector('input[name="options[prefix]"]');
    if (optionsPrefixInput && optionsPrefixInput.value) {
      url.searchParams.set('resources[options][prefix]', optionsPrefixInput.value);
    }

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Original grid HTML cache for restoring after search is cleared.
   * @type {string|null}
   */
  _originalGridHTML = null;

  /**
   * Original product count text cache.
   * @type {string|null}
   */
  _originalCountText = null;

  /**
   * AbortController for cancelling stale collection search API requests.
   * @type {AbortController|null}
   */
  #collectionSearchAbort = null;

  /**
   * Monotonic request counter for stale-response protection.
   * @type {number}
   */
  #collectionSearchVersion = 0;

  /**
   * Escapes HTML entities to prevent XSS when injecting API response data.
   * @param {string} str - Raw string from API response.
   * @returns {string}
   */
  #escapeHTML(str) {
    const el = document.createElement('span');
    el.textContent = str;
    return el.innerHTML;
  }

  /**
   * Updates the result count badge in the search bar and the collection product count.
   * @param {number|null} count - Number of results, or null to hide.
   */
  #updateResultCount(count) {
    const countEl = document.querySelector('.collection-search__count');
    if (countEl) {
      if (count === null || count === undefined) {
        countEl.classList.remove('is-visible');
        countEl.textContent = '';
      } else {
        countEl.textContent = `${count} result${count !== 1 ? 's' : ''}`;
        countEl.classList.add('is-visible');
      }
    }

    const productsCountSpans = document.querySelectorAll('.products-count-wrapper [role="status"]');
    if (productsCountSpans.length > 0) {
      if (count === null || count === undefined) {
        if (this._originalCountText !== null) {
          productsCountSpans.forEach(el => {
            el.textContent = this._originalCountText;
          });
          this._originalCountText = null;
        }
      } else {
        if (this._originalCountText === null) {
          this._originalCountText = productsCountSpans[0].textContent.trim();
        }
        productsCountSpans.forEach(el => {
          el.textContent = `${count} item${count !== 1 ? 's' : ''}`;
        });
      }
    }
  }

  /**
   * Toggles the loading state on the product grid.
   * @param {HTMLElement} grid - The product grid element.
   * @param {boolean} isLoading - Whether to show or hide loading state.
   */
  #setGridLoading(grid, isLoading) {
    if (isLoading) {
      grid.setAttribute('data-search-loading', '');
    } else {
      grid.removeAttribute('data-search-loading');
    }
  }

  /**
   * Filters the collection grid using Shopify's backend Predictive Search API
   * (same engine as the default search bar). Matches against the entire store
   * catalog, not just the products currently loaded on the page.
   *
   * Optimisations:
   *  – AbortController cancels stale in-flight requests on new keystrokes.
   *  – Request versioning guards against race conditions.
   *  – Loading state gives visual feedback.
   *  – Result count badge updates the search bar.
   *  – XSS sanitisation on API response data.
   *  – Injected cards use CSS classes, not inline styles.
   *
   * @param {string} searchTerm - The term to search for.
   */
  async #filterCollectionGrid(searchTerm) {
    const q = searchTerm.trim();
    const productGrid = document.querySelector('#ResultsList ul.product-grid[data-testid="product-grid"]');
    if (!productGrid) return;

    const noResultsEl = document.getElementById('CollectionSearchNoResults');
    const bestsellersEl = document.getElementById('CollectionSearchBestsellers');
    const paginationControls = document.querySelector('#ResultsList .pagination');
    const viewMoreNext = document.querySelector('#ResultsList [ref="viewMoreNext"]');
    const viewMorePrevious = document.querySelector('#ResultsList [ref="viewMorePrevious"]');

    // --- Clear / Reset ---
    if (q === '') {
      // Cancel any in-flight request
      if (this.#collectionSearchAbort) {
        this.#collectionSearchAbort.abort();
        this.#collectionSearchAbort = null;
      }
      this.#hideResetButton();
      this.#updateResultCount(null);
      this.#setGridLoading(productGrid, false);
      // Restore original grid if we cached it
      if (this._originalGridHTML !== null) {
        productGrid.innerHTML = this._originalGridHTML;
        this._originalGridHTML = null;
      }
      productGrid.style.setProperty('display', '', 'important');
      if (noResultsEl) noResultsEl.style.setProperty('display', 'none', 'important');
      if (bestsellersEl) bestsellersEl.style.setProperty('display', 'none', 'important');
      if (paginationControls) paginationControls.style.setProperty('display', '', 'important');
      if (viewMoreNext) viewMoreNext.style.setProperty('display', '', 'important');
      if (viewMorePrevious) viewMorePrevious.style.setProperty('display', '', 'important');

      const url = new URL(window.location.href);
      if (url.searchParams.has('q')) {
        url.searchParams.delete('q');
        history.replaceState({ urlParameters: url.searchParams.toString() }, '', url.toString());
      }
      return;
    }

    this.#showResetButton();

    const url = new URL(window.location.href);
    if (url.searchParams.get('q') !== q) {
      url.searchParams.set('q', q);
      history.replaceState({ urlParameters: url.searchParams.toString() }, '', url.toString());
    }

    // Save original grid before first modification
    if (this._originalGridHTML === null) {
      this._originalGridHTML = productGrid.innerHTML;
    }

    // Always hide pagination during active search
    if (paginationControls) paginationControls.style.setProperty('display', 'none', 'important');
    if (viewMoreNext) viewMoreNext.style.setProperty('display', 'none', 'important');
    if (viewMorePrevious) viewMorePrevious.style.setProperty('display', 'none', 'important');

    // --- Abort previous request & bump version ---
    if (this.#collectionSearchAbort) {
      this.#collectionSearchAbort.abort();
    }
    const abortCtrl = new AbortController();
    this.#collectionSearchAbort = abortCtrl;
    const requestVersion = ++this.#collectionSearchVersion;

    // Show loading state
    this.#setGridLoading(productGrid, true);

    try {
      // --- Fetch from Shopify's backend Predictive Search API ---
      const url = new URL('/search/suggest.json', location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '20');
      url.searchParams.set('resources[options][fields]', 'title,tag,product_type,body,variants.title');

      const response = await fetch(url.toString(), { signal: abortCtrl.signal });
      if (!response.ok) throw new Error(`Search API error: ${response.status}`);

      // --- Stale-response guard ---
      if (requestVersion !== this.#collectionSearchVersion) return;

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      // Remove loading state
      this.#setGridLoading(productGrid, false);

      if (products.length === 0) {
        productGrid.style.setProperty('display', 'none', 'important');
        if (noResultsEl) noResultsEl.style.setProperty('display', 'block', 'important');
        if (bestsellersEl) bestsellersEl.style.setProperty('display', 'block', 'important');
        this.#updateResultCount(0);
        return;
      }

      // --- Match backend results against loaded grid items ---
      const matchedIds = new Set(products.map(p => String(p.id)));
      const items = Array.from(productGrid.querySelectorAll('.product-grid__item:not(.product-grid__item--search-result)'));
      let matchCount = 0;

      items.forEach(item => {
        const productId = item.getAttribute('data-product-id');
        if (productId && matchedIds.has(productId)) {
          item.style.setProperty('display', '', 'important');
          item.style.setProperty('opacity', '1', 'important');
          matchCount++;
          matchedIds.delete(productId);
        } else {
          item.style.setProperty('display', 'none', 'important');
        }
      });

      // --- Render cards for products NOT on the current page ---
      productGrid.querySelectorAll('.product-grid__item--search-result').forEach(el => el.remove());

      const urlParams = new URLSearchParams(window.location.search);
      const hasActiveFilters = Array.from(urlParams.keys()).some(key => key.startsWith('filter.'));

      if (matchedIds.size > 0 && !hasActiveFilters) {
        const fragment = document.createDocumentFragment();
        const unrenderedProducts = products.filter(p => matchedIds.has(String(p.id)));

        unrenderedProducts.forEach(product => {
          const li = document.createElement('li');
          li.className = 'product-grid__item product-grid__item--search-result';
          li.setAttribute('data-product-id', String(product.id));

          const safeTitle = this.#escapeHTML(product.title || '');
          const safeUrl = this.#escapeHTML(product.url || '');
          const imageUrl = product.image
            ? product.image.replace(/(\.[a-z]+)(\?|$)/, '_500x$1$2')
            : '';
          const priceFormatted = product.price
            ? (typeof product.price === 'string' ? product.price : `$${(product.price / 100).toFixed(2)}`)
            : '';

          li.innerHTML = `
            <a href="${safeUrl}" class="product-grid__search-card">
              ${imageUrl ? `<div class="product-grid__search-card-media">
                <img src="${imageUrl}" alt="${safeTitle}" loading="lazy">
              </div>` : ''}
              <div class="product-grid__search-card-info">
                <p class="product-grid__search-card-title">${safeTitle}</p>
                ${priceFormatted ? `<p class="product-grid__search-card-price">${priceFormatted}</p>` : ''}
              </div>
            </a>
          `;
          fragment.appendChild(li);
          matchCount++;
        });

        productGrid.appendChild(fragment);
      }

      // --- Show / hide grid and fallback ---
      if (matchCount > 0) {
        productGrid.style.setProperty('display', '', 'important');
        if (noResultsEl) noResultsEl.style.setProperty('display', 'none', 'important');
        if (bestsellersEl) bestsellersEl.style.setProperty('display', 'none', 'important');
      } else {
        productGrid.style.setProperty('display', 'none', 'important');
        if (noResultsEl) noResultsEl.style.setProperty('display', 'block', 'important');
        if (bestsellersEl) bestsellersEl.style.setProperty('display', 'block', 'important');
      }

      this.#updateResultCount(matchCount);
    } catch (error) {
      // Aborted requests are expected — don't treat as errors
      if (error.name === 'AbortError') return;

      this.#setGridLoading(productGrid, false);

      // Fallback to client-side filtering if API fails
      console.warn('Collection search API fallback:', error);
      const lowerQ = q.toLowerCase();
      const items = Array.from(productGrid.querySelectorAll('.product-grid__item:not(.product-grid__item--search-result)'));
      let matchCount = 0;

      items.forEach(item => {
        const title = (item.getAttribute('data-title') || '').toLowerCase();
        const tags = (item.getAttribute('data-tags') || '').toLowerCase();
        const description = (item.getAttribute('data-description') || '').toLowerCase();

        if (title.includes(lowerQ) || tags.includes(lowerQ) || description.includes(lowerQ)) {
          item.style.setProperty('display', '', 'important');
          item.style.setProperty('opacity', '1', 'important');
          matchCount++;
        } else {
          item.style.setProperty('display', 'none', 'important');
        }
      });

      if (matchCount === 0) {
        productGrid.style.setProperty('display', 'none', 'important');
        if (noResultsEl) noResultsEl.style.setProperty('display', 'block', 'important');
        if (bestsellersEl) bestsellersEl.style.setProperty('display', 'block', 'important');
      } else {
        productGrid.style.setProperty('display', '', 'important');
        if (noResultsEl) noResultsEl.style.setProperty('display', 'none', 'important');
        if (bestsellersEl) bestsellersEl.style.setProperty('display', 'none', 'important');
      }

      this.#updateResultCount(matchCount);
    }
  }

  #handleSectionRender = (event) => {
    if (this.refs.searchInput.id !== 'CollectionSearchInput') return;
    const searchTerm = this.refs.searchInput.value.trim();
    if (searchTerm) {
      const productGrid = document.querySelector('#ResultsList ul.product-grid[data-testid="product-grid"]');
      if (productGrid) {
        this._originalGridHTML = productGrid.innerHTML;
      }
      this.#filterCollectionGrid(searchTerm);
    } else {
      this._originalGridHTML = null;
    }
  };

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    const isCollectionSearch = this.refs.searchInput.id === 'CollectionSearchInput';
    if (isCollectionSearch) {
      this.#currentIndex = -1;
      searchInput.value = '';
      this.#hideResetButton();
      this.#filterCollectionGrid('');
      return;
    }

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
